import { jwtVerify, createRemoteJWKSet, customFetch } from 'jose'
import { jwtDecode } from 'jwt-decode'

import { captureException } from '@surglogs-app/error-reporting'
import { AuthScope } from '@surglogs-app/network'

export type DecodedJwtToken = {
  auth_time: number
  client_id: string
  email: string
  event_id: string
  exp: number
  iat: number
  iss: string
  jti: string
  origin_jti: string
  scope: string
  sub: string
  surglogs_scope: AuthScope
  surglogs_user_id: string
  /* comma separated string of clinic ids */
  surglogs_device_only_clinic_ids: string
  token_use: string
  username: string
}

const LENGTH_OF_DEPRECATED_TOKEN = 30

export const isJwtToken = (token: string): boolean =>
  token.length > LENGTH_OF_DEPRECATED_TOKEN

export const decodeJwtToken = (token: string) => {
  if (!isJwtToken(token)) {
    // If the token is too short, it is a deprecated token format.
    // In this case, we return null to indicate that the token cannot be decoded.
    // This can be removed once we fully migrate to the new JWT format.
    return null
  }

  return jwtDecode<DecodedJwtToken>(token)
}

export const isExternalUserToken = (token: string | null): boolean => {
  if (!token) return false

  return decodeJwtToken(token)?.surglogs_scope === 'EXTERNAL'
}

export const decodeOnlyDeviceClinicIdsFromJwtToken = (
  token: string,
): number[] => {
  try {
    const decodedToken = decodeJwtToken(token)
    const rawDeviceOnlyClinicIds = decodedToken?.surglogs_device_only_clinic_ids

    if (!rawDeviceOnlyClinicIds) {
      return []
    }

    return rawDeviceOnlyClinicIds
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((id) => Number.isInteger(id) && id > 0)
  } catch (err) {
    captureException(err, {
      data: {
        info: 'Failed to verify or decode JWT token for device only clinic ids',
      },
    })

    return []
  }
}

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

async function fetchJwksWithRetry(
  url: string,
  options?: RequestInit,
  attempt = 0,
): Promise<Response> {
  try {
    const response = await fetch(url, options)

    if (!response.ok) {
      throw new Error(`Fetch error: ${response.status} ${response.statusText}`)
    }
    return response
  } catch (err) {
    if (attempt >= MAX_RETRIES - 1) {
      throw err
    }

    await new Promise((res) => setTimeout(res, RETRY_DELAY))

    return fetchJwksWithRetry(url, options, attempt + 1)
  }
}

export const verifyAndDecodeJwtToken = async (
  token: string,
  jwksUrl: string,
): Promise<DecodedJwtToken | null> => {
  if (typeof window !== 'undefined' && (window.Cypress || window.Playwright)) {
    return decodeJwtToken(token)
  }

  try {
    const jwksSet = createRemoteJWKSet(new URL(jwksUrl), {
      [customFetch]: fetchJwksWithRetry,
    })

    const { payload } = await jwtVerify<DecodedJwtToken>(token, jwksSet, {
      algorithms: ['RS256'],
    })

    return payload
  } catch (err) {
    captureException(err, {
      data: { info: 'Failed to verify or decode JWT token' },
    })

    return null
  }
}
